import { useEffect, useState, useCallback } from "react";
import { Soup, ShieldAlert, Users, MessageSquare } from "lucide-react";
import { getBoard } from "../lib/api";
import { Reveal } from "./Reveal";

export const Board = () => {
  const [board, setBoard] = useState({ soups: [], allergies: [], going: [], households: 0, guests: 0 });

  const refresh = useCallback(() => {
    getBoard().then(setBoard).catch(() => {});
  }, []);

  useEffect(() => {
    refresh();
    const interval = setInterval(refresh, 15000);
    const onUpdate = () => refresh();
    window.addEventListener("rsvp-updated", onUpdate);
    return () => {
      clearInterval(interval);
      window.removeEventListener("rsvp-updated", onUpdate);
    };
  }, [refresh]);

  return (
    <section className="relative bg-[#5C6B3C] px-6 md:px-16 lg:px-24 py-24 md:py-36" data-testid="board-section">
      <Reveal x={-80}>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <p className="font-body text-[11px] uppercase tracking-[0.35em] text-cream/70 mb-4">The Living Board</p>
            <h2 className="font-display text-3xl md:text-5xl leading-tight tracking-tight text-cream">
              Claimed pots & <span className="italic text-harvest">care notes</span>
            </h2>
          </div>
          <div className="flex items-center gap-3 text-cream/80 font-body text-sm" data-testid="board-headcount">
            <Users size={18} className="text-ember" />
            <span>
              <strong className="text-cream">{board.guests}</strong> guests across <strong className="text-cream">{board.households}</strong> households
            </span>
          </div>
        </div>
      </Reveal>

      <div className="mt-14 md:mt-20 grid grid-cols-1 lg:grid-cols-12 gap-5">
        <Reveal className="lg:col-span-7" delay={0.05}>
          <div className="notice-block p-8 md:p-12 h-full">
            <div className="flex items-center gap-3 mb-8">
              <Soup className="text-harvest" size={24} />
              <h3 className="font-display text-2xl md:text-3xl text-cream">Soups Already Claimed</h3>
            </div>
            {board.soups.length === 0 ? (
              <p className="font-accent text-3xl text-fog -rotate-1" data-testid="soups-empty">no pots claimed yet — be the first!</p>
            ) : (
              <ul className="space-y-4" data-testid="soups-claimed-list">
                {board.soups.map((entry, i) => (
                  <li key={`${entry.name}-${i}`} className="flex items-baseline gap-4 border-b border-forest/30 pb-4">
                    <span className="font-display text-fog text-sm w-7 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                    <span className="font-display italic text-2xl md:text-3xl text-cream leading-snug">{entry.soup}</span>
                    <span className="font-body text-[11px] uppercase tracking-[0.2em] text-fog ml-auto shrink-0">— {entry.name}</span>
                  </li>
                ))}
              </ul>
            )}
            <p className="mt-8 font-body text-xs text-fog/70 uppercase tracking-[0.2em]">Check here before you pick your recipe — duplicates happen, variety wins.</p>
          </div>
        </Reveal>

        <Reveal className="lg:col-span-5" delay={0.12}>
          <div className="border-2 border-harvest bg-moss/70 backdrop-blur-md p-8 md:p-12 h-full">
            <div className="flex items-center gap-3 mb-8">
              <ShieldAlert className="text-harvest" size={24} />
              <h3 className="font-display text-2xl md:text-3xl text-cream">Allergies</h3>
            </div>
            {board.allergies.length === 0 ? (
              <p className="font-accent text-3xl text-fog -rotate-1" data-testid="allergies-empty">none reported so far</p>
            ) : (
              <ul className="space-y-3" data-testid="allergies-public-list">
                {board.allergies.map((entry, i) => (
                  <li key={`${entry.name}-${i}`} className="flex items-start gap-3">
                    <span className="mt-2 h-1.5 w-1.5 rounded-full bg-harvest shrink-0" />
                    <span className="font-body text-base text-cream leading-relaxed">
                      <strong className="font-display italic font-semibold text-harvest">{entry.allergies}</strong>
                      <span className="text-fog"> — {entry.name}</span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <p className="mt-8 font-body text-xs text-fog/70 uppercase tracking-[0.2em]">Cook with these in mind so everyone gets a bowl.</p>
          </div>
        </Reveal>
      </div>

      <Reveal delay={0.18} className="mt-5">
        <div className="notice-block p-8 md:p-12" data-testid="whos-going">
          <div className="flex items-center gap-3 mb-8">
            <Users className="text-ember" size={24} />
            <h3 className="font-display text-2xl md:text-3xl text-cream">Who's Going</h3>
          </div>
          {board.going.length === 0 ? (
            <p className="font-accent text-3xl text-fog -rotate-1" data-testid="going-empty">the guest list is waiting for its first name</p>
          ) : (
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8" data-testid="going-list">
              {board.going.map((house, i) => (
                <li key={`${house.name}-${i}`} className="border-b border-forest/30 pb-6" data-testid={`going-row-${i}`}>
                  <div className="flex items-baseline justify-between gap-4">
                    <span className="font-display text-xl md:text-2xl text-cream">{house.name}</span>
                    <span className="font-body text-[11px] uppercase tracking-[0.2em] text-fog shrink-0">
                      party of {house.party_size}
                    </span>
                  </div>
                  {house.attendees.length > 0 && (
                    <ul className="mt-3 space-y-1.5">
                      {house.attendees.map((a, j) => (
                        <li key={j} className="font-body text-sm text-fog">
                          {a.name || "Guest"}
                          {a.soup && (
                            <span className="text-cream">
                              {" "}— bringing <span className="font-display italic text-base text-harvest">{a.soup}</span>
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                  {house.notes && (
                    <p className="mt-3 font-body text-sm text-fog italic flex items-start gap-2">
                      <MessageSquare size={13} className="text-ember mt-1 shrink-0" />
                      "{house.notes}"
                    </p>
                  )}
                </li>
              ))}
            </ul>
          )}
          <p className="mt-8 font-body text-xs text-fog/70 uppercase tracking-[0.2em]">Names, soups, and any extra notes from each RSVP land here.</p>
        </div>
      </Reveal>
    </section>
  );
};
